<template>
  <el-input
    v-if="field.type === 'input'"
    :model-value="value"
    :placeholder="field.placeholder"
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @update:model-value="$emit('update', $event)"
    @clear="$emit('update', null)"
  />

  <el-input-number
    v-else-if="field.type === 'input_number'"
    ref="inputRef"
    :model-value="value"
    :placeholder="field.placeholder"
    :controls="false"
    :disabled="field.disabled"
    :formatter="formatter"
    :parser="parser"
    size="default"
    style="width: 100%"
    @focus="handleFocus"
    @blur="handleBlur(value)"
    @update:model-value="$emit('update', $event)"
  />

  <!-- select con búsqueda remota (remote-method) -->
  <el-select
    v-else-if="field.type === 'select' && field.searchFn"
    :model-value="value"
    :multiple="field.multiple"
    :loading="remoteLoading"
    :placeholder="field.placeholder || 'Buscar...'"
    :value-key="field.fieldKey || 'Codigo'"
    :collapse-tags="field.multiple"
    :collapse-tags-tooltip="field.multiple"
    remote
    filterable
    :disabled="field.disabled"
    clearable
    reserve-keyword
    size="default"
    style="width: 100%"
    :remote-method="handleRemoteSearch"
    @update:model-value="$emit('update', $event)"
    @clear="$emit('update', field.multiple ? [] : null)"
  >
    <el-option
      v-for="opt in remoteOptions"
      :key="opt[field.fieldKey || 'Codigo']"
      :label="opt[field.fieldLabel || 'Descripcion']"
      :value="opt"
    />
    <template #empty>
      <div class="sf-control-loading">
        {{ remoteLoading ? 'Buscando...' : `Escribí al menos ${field.minChars ?? 3} letras…` }}
      </div>
    </template>
  </el-select>

  <!-- select estático o con fetchOptions (lazy load al abrir) -->
  <el-select
    v-else-if="field.type === 'select'"
    :model-value="value"
    :multiple="field.multiple"
    :loading="loading"
    :placeholder="field.placeholder || 'Seleccionar'"
    :value-key="field.fieldKey || 'Codigo'"
    :collapse-tags="field.multiple"
    :collapse-tags-tooltip="field.multiple"
    filterable
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @update:model-value="$emit('update', $event)"
    @visible-change="(visible) => visible && $emit('open')"
    @clear="$emit('update', field.multiple ? [] : null)"
  >
    <el-option
      v-for="opt in options"
      :key="opt[field.fieldKey || 'Codigo']"
      :label="opt[field.fieldLabel || 'Descripcion']"
      :value="opt"
    />
    <template
      v-if="loading"
      #empty
    >
      <div class="sf-control-loading">Cargando...</div>
    </template>
  </el-select>

  <el-autocomplete
    v-else-if="field.type === 'autocomplete'"
    :model-value="displayAutocompleteValue"
    :fetch-suggestions="handleAutoSearch"
    :placeholder="field.placeholder || 'Buscar...'"
    :value-key="field.fieldLabel || 'Descripcion'"
    :debounce="300"
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @input="$emit('update', $event)"
    @select="$emit('update', $event)"
    @clear="$emit('update', null)"
  >
    <template #default="{ item }">
      <slot
        name="autocomplete-option"
        :item="item"
      >
        <span>{{ item[field.fieldLabel || 'Descripcion'] }}</span>
      </slot>
    </template>
  </el-autocomplete>

  <el-date-picker
    v-else-if="field.type === 'date'"
    :model-value="value"
    type="date"
    :placeholder="field.placeholder || 'Seleccionar fecha'"
    format="DD/MM/YYYY"
    value-format="YYYY-MM-DD"
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @update:model-value="$emit('update', $event)"
    @clear="$emit('update', null)"
  />

  <el-date-picker
    v-else-if="field.type === 'date_range'"
    :model-value="value"
    type="daterange"
    range-separator="—"
    start-placeholder="Desde"
    end-placeholder="Hasta"
    format="DD/MM/YYYY"
    value-format="YYYY-MM-DD"
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @update:model-value="$emit('update', $event)"
    @clear="$emit('update', null)"
  />

  <el-input
    v-else-if="field.type === 'select_input'"
    :model-value="value?.[1] ?? null"
    :placeholder="field.placeholderInput || field.placeholder || 'Valor'"
    :disabled="field.disabled"
    clearable
    size="default"
    style="width: 100%"
    @update:model-value="$emit('update', [value?.[0] ?? null, $event || null])"
    @clear="$emit('update', [value?.[0] ?? null, null])"
  >
    <template #prepend>
      <el-select
        :model-value="value?.[0] ?? null"
        :loading="loading"
        :placeholder="field.placeholderSelect || '—'"
        :value-key="field.fieldKey || 'Codigo'"
        :disabled="field.disabled"
        filterable
        clearable
        size="default"
        :style="{ width: (field.selectWidth || 140) + 'px' }"
        @update:model-value="$emit('update', [$event, value?.[1] ?? null])"
        @visible-change="(visible) => visible && $emit('open')"
        @clear="$emit('update', [null, value?.[1] ?? null])"
      >
        <el-option
          v-for="opt in options"
          :key="opt[field.fieldKey || 'Codigo']"
          :label="opt[field.fieldLabel || 'Descripcion']"
          :value="opt"
        />
        <template
          v-if="loading"
          #empty
        >
          <div class="sf-control-loading">Cargando...</div>
        </template>
      </el-select>
    </template>
  </el-input>

  <el-switch
    v-else-if="field.type === 'switch'"
    :disabled="field.disabled"
    :model-value="value"
    active-text="Sí"
    inactive-text="No"
    @update:model-value="$emit('update', $event)"
  />
</template>

<script setup>
import {
  computed,
  ref,
} from 'vue';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(null);

const props = defineProps({
  field: { type: Object, required: true },
  value: { default: null },
  options: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['update', 'open']);

const remoteOptions = ref([]);
const remoteLoading = ref(false);

const handleRemoteSearch = async (query) => {
  if (!props.field.searchFn) return;
  const min = props.field.minChars ?? 3;
  if (!query || query.length < min) {
    remoteOptions.value = [];
    return;
  }
  remoteLoading.value = true;
  try {
    remoteOptions.value = (await props.field.searchFn(query)) || [];
  } catch {
    remoteOptions.value = [];
  } finally {
    remoteLoading.value = false;
  }
};

const displayAutocompleteValue = computed(() => {
  if (typeof props.value === 'object' && props.value !== null) {
    return props.value[props.field.fieldLabel || 'Descripcion'] || '';
  }
  return props.value || '';
});

const handleAutoSearch = async (query, callback) => {
  if (!props.field.searchFn) {
    callback([]);
    return;
  }
  const minChars = props.field.minChars ?? 3;
  if (!query || query.length < minChars) {
    callback([]);
    return;
  }
  try {
    const results = await props.field.searchFn(query);
    callback(results || []);
  } catch {
    callback([]);
  }
};
</script>

<style scoped>
.sf-control-loading {
  padding: 8px;
  color: #888;
  font-size: 13px;
  text-align: center;
}
</style>
